import React, { useCallback, useEffect, useState } from 'react';
import ContentBox from '../components/ContentBox';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Use the CDN-hosted worker — avoids CRA bundler worker config complexity
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_API_URL;

function IssueButton({ issue, isActive, onClick }) {
    return (
        <button
            onClick={() => onClick(issue)}
            className={`w-full text-left font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-2 rounded transition-all
                ${isActive ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
        >
            Issue #{issue.issueNumber}
            <div className={`text-[11px] font-normal normal-case tracking-normal mt-0.5 ${isActive ? 'text-red-100' : 'text-gray-500'}`}>
                {issue.title} &middot;{' '}
                {new Date(issue.publicDate).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                })}
            </div>
        </button>
    );
}

function PdfViewer({ issue }) {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [containerWidth, setContainerWidth] = useState(800);
    const containerRef = useCallback((node) => {
        if (node) setContainerWidth(node.getBoundingClientRect().width);
    }, []);

    // Reset to page 1 when issue changes
    useEffect(() => {
        setCurrentPage(1);
        setNumPages(null);
    }, [issue?.id]);

    if (!issue) {
        return (
            <div className="flex items-center justify-center h-[600px] bg-gray-50 border border-gray-200">
                <p className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] text-gray-400">
                    Select an issue to read
                </p>
            </div>
        );
    }

    const goTo = (page) => {
        if (page < 1 || page > (numPages || 1)) return;
        setCurrentPage(page);
    };

    const PaginationControls = () => (
        <div className="flex items-center gap-2 justify-center">
            <button
                onClick={() => goTo(currentPage - 1)}
                disabled={currentPage <= 1}
                className="bg-gray-200 text-gray-700 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
            >
                ← Prev
            </button>
            <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-600 min-w-[80px] text-center">
                {currentPage} / {numPages}
            </span>
            <button
                onClick={() => goTo(currentPage + 1)}
                disabled={currentPage >= numPages}
                className="bg-gray-200 text-gray-700 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.15)' }}
            >
                Next →
            </button>
        </div>
    );

    return (
        <div className="flex flex-col gap-3">

            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2
                        className="font-['Jersey_10'] uppercase text-[20px] tracking-[1px] text-maroon"
                        style={{ textShadow: 'none' }}
                    >
                        Issue #{issue.issueNumber} — {issue.title}
                    </h2>
                    <p className="text-[12px] text-gray-500 mt-0.5">
                        Published{' '}
                        {new Date(issue.publicDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                        })}
                        {issue.uploadedBy && ` · Uploaded by ${issue.uploadedBy}`}
                    </p>
                </div>
                <a
                    href={issue.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-maroon text-white font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-4 py-2 hover:opacity-80 transition-opacity shrink-0"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                >
                    Open PDF ↗
                </a>
            </div>

            {numPages && numPages > 1 && <PaginationControls />}

            {/* PDF canvas */}
            <div
                ref={containerRef}
                className="border border-gray-300 bg-gray-100 flex justify-center overflow-x-auto"
            >
                <Document
                    key={issue.id}
                    file={issue.pdfUrl}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    loading={
                        <div className="flex items-center justify-center py-20 w-full">
                            <p className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-gray-400 animate-pulse">
                                Loading PDF…
                            </p>
                        </div>
                    }
                    error={
                        <div className="flex flex-col items-center justify-center py-20 w-full gap-3">
                            <p className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-red-500">
                                Could not load PDF
                            </p>
                            <a
                                href={issue.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-maroon text-white font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-4 py-2 hover:opacity-80 transition-opacity"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)' }}
                            >
                                Open PDF directly ↗
                            </a>
                        </div>
                    }
                >
                    <Page
                        pageNumber={currentPage}
                        width={Math.max(containerWidth - 32, 612)}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                    />
                </Document>
            </div>

            {numPages && numPages > 1 && <PaginationControls />}
        </div>
    );
}

function OriginsObserver() {
    const [issues, setIssues] = useState([]);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const loadIssues = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}observer/issues`);
            const data = await response.json();
            if (data.success && data.issues.length > 0) {
                setIssues(data.issues);

                const requestedNumber = parseInt(searchParams.get('issue'), 10);
                const target = requestedNumber
                    ? data.issues.find((i) => i.issueNumber === requestedNumber)
                    : null;

                setSelectedIssue(target || data.issues[0]);
            }
        } catch (error) {
            console.error('Failed to load Observer issues:', error);
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadIssues();
    }, [loadIssues]);

    const handleSelectIssue = (issue) => {
        setSelectedIssue(issue);
        setSearchParams({ issue: issue.issueNumber });
    };

    return (
        <div className="w-full min-h-screen py-4 px-2">
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

                {/* Left: description + issue buttons */}
                <div className="flex flex-col gap-4 lg:w-[280px] shrink-0">
                    <ContentBox
                        theme="default"
                        tint="lightskyblue"
                        header="About the Project"
                        icon="img/icons/icon_264.png"
                    >
                        <p className="text-[13px] text-gray-600 mb-3">
                            A community-run periodical dedicated to documenting the stories, events, personalities and culture of Habbo Hotel: Origins. Created in collaboration with Frankly Origins, this project exists to celebrate the creativity and history being shaped across the hotel with every issue.
                        </p>
                        <p className="text-[13px] text-gray-600 mb-3">
                            We're actively looking for contributors of all kinds. Writers, reporters, storytellers, designers, pixel artists and anyone with a passion for Origins are encouraged to get involved.
                        </p>
                        <p className="text-[13px] text-gray-600">
                            If you'd like to contribute or collaborate on a future issue, reach out to us in the{' '}
                            <a
                                href="https://discord.gg/franklyorigins"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-maroon underline hover:opacity-70 transition-opacity font-semibold"
                            >
                                Frankly Origins Discord server
                            </a>
                        </p>
                    </ContentBox>

                    <ContentBox
                        theme="default"
                        tint="lightskyblue"
                        header="The Origins Observer"
                        icon="img/icons/icon_264.png"
                    >
                        {loading ? (
                            <div className="flex flex-col gap-2">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />
                                ))}
                            </div>
                        ) : issues.length === 0 ? (
                            <p className="text-[13px] text-gray-500 uppercase font-['Jersey_10'] tracking-[1px]">
                                No issues yet
                            </p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {issues.map((issue) => (
                                    <IssueButton
                                        key={issue.id}
                                        issue={issue}
                                        isActive={selectedIssue?.id === issue.id}
                                        onClick={handleSelectIssue}
                                    />
                                ))}
                            </div>
                        )}
                    </ContentBox>
                </div>

                {/* Right: PDF reader */}
                <div className="flex-1 min-w-0">
                    <ContentBox theme="default" tint="maroon" header="Read">
                        {loading ? (
                            <div className="h-[600px] bg-gray-100 animate-pulse" />
                        ) : (
                            <PdfViewer issue={selectedIssue} />
                        )}
                    </ContentBox>
                </div>

            </div>
        </div>
    );
}

export default OriginsObserver;